/**
 * Takes over the color assignment of another logo reproduction on the same
 * palette (api/reproduction/assignment/sources, then apply_from). Each
 * candidate says how it transfers: the same design component by
 * component, any other design by source color. ``onApplied`` receives the
 * endpoint's counts.
 */
import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItemButton, ListItemText, Radio, Stack, TextField, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { reproductionModeLabel } from "../helper/reproductionFlow";
import { formatDateOnly } from "../helper/formatDate";

// A search box only once the list is long enough to need one.
const SEARCH_FROM = 7;

/** How a candidate's assignment lands on this reproduction. */
export function transferSummary(source) {
  if (source.matching === "components") return "Same design: taken over component by component";
  if (source.matching === "positions") {
    return `Same artwork: ${source.matched_by_position} components taken over by position, the rest by source color`;
  }
  return `By source color: ${source.matched_colors} of ${source.color_count} source colors match`;
}

/** "Shape fill · colors set for 12 of 14 components · Sep 20, 2026" */
export function sourceSummary(source) {
  const parts = [
    reproductionModeLabel(source.reproduction_mode),
    `colors set for ${source.decided_count} of ${source.component_count} components`,
  ];
  if (source.created_at) parts.push(formatDateOnly(source.created_at));
  return parts.join(" · ");
}

export default function AssignmentReuseDialog({ open, onClose, reproductionId, onApplied }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [applying, setApplying] = useState(false);

  // The candidates are read every time the dialog opens: another tab may
  // have decided a reproduction meanwhile.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setSelectedId(null);
    setFilter("");
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const response = await axios.post("api/reproduction/assignment/sources", {
          reproduction_id: reproductionId,
        });
        if (active) setSources(response.data.sources || []);
      } catch (err) {
        console.error("Error loading the reproductions to reuse:", err);
        if (active) {
          setSources([]);
          setError(err?.response?.data?.error || "Loading the reproductions failed.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, reproductionId]);

  const apply = async () => {
    if (!selectedId) return;
    setApplying(true);
    setError(null);
    try {
      const response = await axios.post("api/reproduction/assignment/apply_from", {
        reproduction_id: reproductionId,
        source_reproduction_id: selectedId,
      });
      await onApplied(response.data);
      onClose();
    } catch (err) {
      console.error("Error reusing the assignment:", err);
      setError(err?.response?.data?.error || "Reusing the assignment failed.");
    } finally {
      setApplying(false);
    }
  };

  const close = () => {
    if (!applying) onClose();
  };
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? sources.filter((source) => (source.name || "").toLowerCase().includes(needle))
    : sources;

  let body;
  if (loading) {
    body = (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={22} thickness={5} aria-label="Loading reproductions" />
      </Box>
    );
  } else if (shown.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary">
        {sources.length === 0
          ? "No other logo reproduction uses this palette."
          : "No reproduction matches the search."}
      </Typography>
    );
  } else {
    body = (
      <List
        dense
        disablePadding
        sx={{ maxHeight: 360, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}
      >
        {shown.map((source) => {
          const checked = source.reproduction_id === selectedId;
          return (
            <ListItemButton
              key={source.reproduction_id}
              selected={checked}
              disabled={applying}
              onClick={() => setSelectedId(source.reproduction_id)}
              sx={{ alignItems: "flex-start" }}
            >
              <Radio
                size="small"
                checked={checked}
                tabIndex={-1}
                sx={{ mt: -0.5, mr: 0.5, p: 0.5 }}
                inputProps={{ "aria-label": `Select ${source.name}` }}
              />
              <ListItemText
                primary={source.name}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondary={`${sourceSummary(source)}\n${transferSummary(source)}`}
                secondaryTypographyProps={{ whiteSpace: "pre-line" }}
              />
            </ListItemButton>
          );
        })}
      </List>
    );
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Reuse a color assignment</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Logo reproductions on the same palette. The same design is taken over component by
            component, any other design by source color; components without a match keep their color.
          </Typography>
          {sources.length >= SEARCH_FROM ? (
            <TextField
              size="small"
              label="Search by name"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              fullWidth
            />
          ) : null}
          {body}
          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={close}
          disabled={applying}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={applying ? <CircularProgress size={16} thickness={5} color="inherit" /> : <ContentCopyIcon />}
          disabled={applying || !selectedId}
          onClick={apply}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {applying ? "Applying..." : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
